import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { PageHeader } from '../../../../components/PageHeader';
import { Button, HEADER_BUTTON_DARK_BLUE } from '../../../../components/PageHeader/Button';
import Table3, { columnHelper } from '../../../../containers/Table/Table3';
import SimpleModal from '../../../../containers/Modal/SimpleModal';
import Select from '../../../../components/Select';
import { notificationActions } from '../../../../components/Notification/redux/actions';
import Strings from '../../../../Strings';
import { showStatus } from '../../../../utils';
import { TextInput } from '../../../../components/Inputs/TextInput';
import { openModalAction } from '../../../../actions/modal';
import NewConfirmationModal from '../../../../modals/NewConfirmationModal/NewConfirmationModal';
import {
  actions,
  GET_ACCESS_SIM_CONFIG_RESULT,
  LIST_ACCESS_SIM_PATIENTS_RESULT,
  LIST_ACCESS_SIM_ORG_PATIENTS_RESULT,
} from './actions';
import {
  PATIENT_OPERATIONS,
  scriptSummary,
} from './constants';
import OrgDefaults from './OrgDefaults';
import PatientModal, { emptyPatientDraft, patientDraftFrom, patientDraftIsValid } from './PatientModal';
import CommLog from './CommLog';
import useAccessOrgSelection from '../useAccessOrgSelection';
import styles from './AccessSimulator.module.scss';

// The simulator answers every operation of a patient; the table shows one
// summary chip per operation, falling back to the org default.
const responseSummary = (patient, defaults) => PATIENT_OPERATIONS.map(operation => {
  const override = patient.responses?.find(item => item.operation === operation && item.seq === 0);
  const summary = scriptSummary(override)
    || scriptSummary(defaults.find(item => item.operation === operation));
  return `${operation}: ${summary || '-'}`;
}).join('  ·  ');

/**
 * Superuser tab driving the CMS ACCESS server simulator: org pointing, organization
 * default answers, simulated patients with their scripted answers and the
 * us↔simulator communication log.
 */
function AccessSimulator({
  userId,
  getConfig, listPatients, listOrgPatients, getMyOrgs, pointOrg, unpointOrg, createPatient, updatePatient, deletePatient,
  setPatientScript, clearPatientScript, setOrgDefault, reset, sendUnalignmentEmail, showNotification, openConfirmModal,
}) {
  const [config, setConfig] = useState(null);
  const [patients, setPatients] = useState([]);
  const [orgPatients, setOrgPatients] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [patientModal, setPatientModal] = useState(null);
  const [unalignModal, setUnalignModal] = useState(null);
  const [fallbackOrg, setFallbackOrg] = useState(false);

  const showError = resp => showNotification(
    resp?.response?.data?.standard_error?.message || Strings.errors.internalError,
    5000,
    true,
  );

  // The org the tab drives. A superuser normally has no org of his own, so it is
  // picked explicitly and sent along with every call (null = my own org).
  const { orgs, orgUUID, setOrgUUID, orgsLoaded } = useAccessOrgSelection(userId, getMyOrgs, showError);

  const load = useCallback(targetOrgUUID => {
    setIsLoading(true);
    return Promise.all([
      getConfig(targetOrgUUID),
      listPatients(targetOrgUUID),
      listOrgPatients(targetOrgUUID),
    ]).then(([configResp, patientsResp, orgPatientsResp]) => {
      let configOk = false;
      if (configResp.type === GET_ACCESS_SIM_CONFIG_RESULT) {
        setConfig(configResp.response);
        configOk = true;
      } else if (configResp.response?.status === 403 || configResp.response?.status === 404) {
        showNotification(Strings.accessSimNotEnabled, 5000, true);
      } else if (configResp.response?.status === 401 && !targetOrgUUID) {
        // The caller has no organization of his own - fall back to the first one
        // he can act on, which re-loads the tab through the orgUUID effect below.
        setFallbackOrg(true);
      } else {
        showError(configResp);
      }
      // Anything other than the result action is a broken call - report it
      // instead of silently showing an empty list. When the config call itself
      // failed (simulator off, or no org selected yet), the list calls fail the
      // same way and their errors would only be noise on top of the one shown.
      const reportListError = (resp, resultType) => {
        if (configOk && resp.type !== resultType) {
          showError(resp);
        }
      };
      const freshPatients = patientsResp.response?.patients || [];
      setPatients(freshPatients);
      reportListError(patientsResp, LIST_ACCESS_SIM_PATIENTS_RESULT);
      setOrgPatients(orgPatientsResp.response?.patients || []);
      reportListError(orgPatientsResp, LIST_ACCESS_SIM_ORG_PATIENTS_RESULT);
      setIsLoading(false);
      return freshPatients;
    });
  }, [getConfig, listPatients, listOrgPatients, showNotification]);

  // Wait for the org list: without it a superuser with no org of his own cannot
  // be offered anything to select.
  useEffect(() => {
    if (orgsLoaded) load(orgUUID);
  }, [orgsLoaded, orgUUID, load]);

  useEffect(() => {
    if (!fallbackOrg || !orgsLoaded || orgUUID || orgs.length === 0) return;
    setFallbackOrg(false);
    setOrgUUID(orgs[0].value);
  }, [fallbackOrg, orgs, orgsLoaded, orgUUID]);

  // run performs a mutating call, reports the outcome and reloads the tab.
  const run = useCallback((call, successMessage, andReload = true) => {
    setIsSaving(true);
    return call().then(resp => {
      if (!resp.type.endsWith('/result')) {
        showError(resp);
      } else if (successMessage) {
        showNotification(successMessage, 5000);
      }
      return andReload ? load(orgUUID).then(() => resp) : resp;
    }).finally(() => setIsSaving(false));
  }, [load, orgUUID]);

  const defaults = config?.defaults || [];

  const openCreatePatient = () => setPatientModal({ patient: null, draft: emptyPatientDraft() });

  const openEditPatient = patient => setPatientModal({ patient, draft: patientDraftFrom(patient) });

  // savePatient writes the identity fields first and then every script the
  // tester touched (set overrides, clear overrides back to the organization
  // default), and reloads the tab once.
  const savePatient = () => {
    const { patient, draft } = patientModal;
    const body = {
      display_name: draft.display_name,
      track: draft.track,
      status: draft.status,
    };
    setIsSaving(true);
    const identity = patient
      ? updatePatient(patient.id, body, orgUUID)
      : createPatient({ ...body, mbi: draft.mbi.trim() }, orgUUID);
    return identity
      .then(resp => {
        if (!resp.type.endsWith('/result')) return showError(resp);
        const targetId = patient ? patient.id : resp.response?.id;
        if (!targetId) return undefined;
        const pending = [];
        PATIENT_OPERATIONS.forEach(operation => {
          const script = draft.scripts[operation];
          if (script) {
            pending.push({ operation, promise: setPatientScript(targetId, operation, { ...script, operation }, orgUUID) });
          } else if (draft.cleared.includes(operation)) {
            pending.push({ operation, promise: clearPatientScript(targetId, operation, undefined, orgUUID) });
          }
        });
        return Promise.allSettled(pending.map(item => item.promise)).then(results => {
          const failed = [];
          const applied = [];
          pending.forEach((item, index) => {
            const result = results[index];
            if (result.status === 'fulfilled' && result.value.type.endsWith('/result')) {
              applied.push(item.operation);
            } else {
              failed.push(item.operation);
            }
          });
          return load(orgUUID).then(freshPatients => {
            if (failed.length === 0) {
              setPatientModal(null);
              return;
            }
            // Partial write: report which operations failed and keep the modal
            // open with only the failed operations still editable.
            showNotification(`${Strings.accessSimPartialSave}: ${failed.join(', ')}`, 5000, true);
            const freshPatient = freshPatients.find(item => item.id === targetId);
            const nextDraft = { ...draft, scripts: { ...draft.scripts }, cleared: [...draft.cleared] };
            applied.forEach(operation => {
              delete nextDraft.scripts[operation];
              nextDraft.cleared = nextDraft.cleared.filter(item => item !== operation);
            });
            setPatientModal({ patient: freshPatient || patient, draft: nextDraft });
          });
        });
      })
      .finally(() => setIsSaving(false));
  };

  const removePatient = patient => {
    openConfirmModal({
      text: Strings.accessSimDeletePatientConfirm,
      onConfirm: () => run(() => deletePatient(patient.id, orgUUID)),
    });
  };

  const sendUnalignment = () => {
    const body = {};
    if (unalignModal.submission_id) body.submission_id = unalignModal.submission_id;
    else if (unalignModal.patient_id) body.patient_id = unalignModal.patient_id;
    else {
      showNotification(Strings.accessSimSendUnalignmentEmailNeedsPatient, 5000, true);
      return undefined;
    }
    return run(() => sendUnalignmentEmail(body, orgUUID), Strings.accessSimSendUnalignmentEmailDone)
      .then(() => setUnalignModal(null));
  };

  const columns = [
    columnHelper.accessor('id', { header: Strings.accessSimPatientId, size: 80 }),
    columnHelper.accessor('mbi', { header: Strings.accessSimMbi, size: 190 }),
    columnHelper.accessor('display_name', { header: Strings.accessSimDisplayName, size: 180, cell: info => info.getValue() || '-' }),
    columnHelper.accessor('track', { header: Strings.accessSimTrack, size: 90, cell: info => info.getValue() || '-' }),
    columnHelper.accessor('status', { header: Strings.accessSimStatus, size: 130, enableSorting: false, cell: info => showStatus(info.getValue()) }),
    columnHelper.accessor('responses', {
      header: Strings.accessSimResponses,
      size: 460,
      enableSorting: false,
      cell: info => responseSummary(info.row.original, defaults),
    }),
  ];

  const patientOptions = patients.map(patient => ({
    value: patient.id,
    label: patient.display_name ? `${patient.id} · ${patient.display_name}` : `${patient.id}`,
  }));

  return (
    <>
      <PageHeader
        left={Strings.accessSimulatorTab}
        right={(
          <>
            <Button
              class={HEADER_BUTTON_DARK_BLUE}
              title={Strings.accessSimReset}
              onClick={() => openConfirmModal({
                text: Strings.accessSimResetConfirm,
                onConfirm: () => run(() => reset(orgUUID), Strings.accessSimResetDone),
              })}
              disabled={isSaving || (!orgUUID && !orgs.length)}
              testid="reset-access-simulator"
            />
            {config?.pointed_at_simulator ? (
              <Button
                class={HEADER_BUTTON_DARK_BLUE}
                title={Strings.accessSimUnpointOrg}
                onClick={() => run(() => unpointOrg(orgUUID))}
                disabled={isSaving}
                testid="unpoint-access-simulator"
              />
            ) : (
              <Button
                class={HEADER_BUTTON_DARK_BLUE}
                title={Strings.accessSimPointOrg}
                onClick={() => run(() => pointOrg({}, orgUUID))}
                disabled={isSaving}
                testid="point-access-simulator"
              />
            )}
          </>
        )}
      />

      <div className={styles.accessSimulator}>
        <div className={styles.accessSimulatorOrg}>
          <span className={styles.accessSimulatorFieldLabel}>{Strings.accessSimOrg}</span>
          <Select
            id="access-simulator-org"
            data={orgs}
            value={orgUUID}
            onChange={option => setOrgUUID(option?.value ?? null)}
            isSearchable
            disabled={isSaving}
          />
        </div>
        <h2>{config?.pointed_at_simulator ? Strings.accessSimPointed : Strings.accessSimNotPointed}</h2>
        <div className={styles.accessSimulatorStatus}>
          <div className={styles.accessSimulatorField}>
            <span data-tooltip-id="tooltip" data-tooltip-content={Strings.accessSimBaseUrlTooltip}>{Strings.accessSimBaseUrl}</span>
            <span className={styles.accessSimulatorValue}>
              {config?.simulator_url || '-'}
            </span>
          </div>
          <div className={styles.accessSimulatorField}>
            {Strings.accessSimEnvironment}
            <span className={styles.accessSimulatorValue}>
              {(config?.configs || []).map(item => `${item.environment}: ${item.enabled ? Strings.accessSimConfigured : Strings.accessSimNotConfigured}`).join(', ')
                || Strings.accessSimNotConfigured}
            </span>
          </div>
          <div className={styles.accessSimulatorField}>
            <span data-tooltip-id="tooltip" data-tooltip-content={Strings.accessSimOauthPolicyTooltip}>{Strings.accessSimOauthPolicy}</span>
            <span className={styles.accessSimulatorValue}>
              {`${config?.oauth_policy || '-'}${config?.oauth_outage ? ` · ${Strings.accessSimOauthOutage}` : ''}`}
            </span>
          </div>
          <div className={styles.accessSimulatorField}>
            <span data-tooltip-id="tooltip" data-tooltip-content={Strings.accessSimMailboxTooltip}>{Strings.accessSimMailbox}</span>
            <span className={styles.accessSimulatorValue}>{config?.mailbox_address || '-'}</span>
          </div>
        </div>
        <p className={styles.accessSimulatorNote}>{Strings.accessSimCloudTimeNote}</p>
      </div>

      <div className={styles.accessSimulatorTable}>
        <div className={styles.accessSimulatorSectionHeader}>
          <h2>{Strings.accessSimPatients}</h2>
          <div className={styles.accessSimulatorSectionActions}>
            <Button
              class={HEADER_BUTTON_DARK_BLUE}
              title={Strings.accessSimSendUnalignmentEmail}
              onClick={() => setUnalignModal({ patient_id: null, submission_id: '' })}
              disabled={isSaving}
              testid="inject-unalignment-email"
            />
            <Button
              class={HEADER_BUTTON_DARK_BLUE}
              title={Strings.accessSimAddPatient}
              onClick={openCreatePatient}
              disabled={isSaving}
              testid="add-access-sim-patient"
            />
          </div>
        </div>
        <Table3
          name="access-simulator-patients"
          data={patients}
          columns={columns}
          isLoading={isLoading}
          noPagination
          enableRowExpansion
          renderExpandedRow={patient => <CommLog patient={patient} orgUUID={orgUUID} />}
          buttons={[
            { icon: 'edit', onClick: (rowId, patient) => openEditPatient(patient), testId: 'edit-access-sim-patient' },
            { icon: 'delete', onClick: (rowId, patient) => removePatient(patient), testId: 'delete-access-sim-patient' },
          ]}
        />
      </div>

      <div className={styles.accessSimulatorTable}>
        <h2>{Strings.accessSimOrgDefaults}</h2>
        <OrgDefaults
          defaults={defaults}
          onSave={script => run(() => setOrgDefault(script, orgUUID))}
          disabled={isSaving}
        />
      </div>

      {patientModal && (
        <SimpleModal
          name="access-simulator-patient"
          title={patientModal.patient ? Strings.accessSimPatients : Strings.accessSimAddPatient}
          onCancel={() => setPatientModal(null)}
          onSubmit={savePatient}
          isSubmitDisabled={isSaving || !patientDraftIsValid(patientModal.draft)}
          content={(
            <PatientModal
              patient={patientModal.patient}
              defaults={defaults}
              orgPatients={orgPatients}
              draft={patientModal.draft}
              setDraft={draft => setPatientModal({ ...patientModal, draft })}
              isSaving={isSaving}
            />
          )}
        />
      )}

      {unalignModal && (
        <SimpleModal
          name="access-simulator-unalignment-email"
          title={Strings.accessSimSendUnalignmentEmail}
          onCancel={() => setUnalignModal(null)}
          onSubmit={sendUnalignment}
          isSubmitDisabled={isSaving}
          content={(
            <div className={styles.unalignModal}>
              <div className={styles.unalignModalField}>
                <span>{Strings.patient}</span>
                <Select
                  id="access-simulator-unalign-patient"
                  data={patientOptions}
                  value={unalignModal.patient_id}
                  onChange={option => setUnalignModal({ ...unalignModal, patient_id: option?.value ?? null })}
                  disabled={isSaving}
                />
              </div>
              <label className={styles.unalignModalField} htmlFor="access-simulator-unalign-submission">
                {Strings.accessSimSubmissionId}
                <TextInput
                  id="access-simulator-unalign-submission"
                  type="text"
                  value={unalignModal.submission_id}
                  onChange={event => setUnalignModal({ ...unalignModal, submission_id: event.target.value })}
                  disabled={isSaving}
                />
              </label>
              <p className={styles.unalignModalHint}>
                {`${Strings.accessSimMailbox}: ${config?.mailbox_address || '-'}`}
              </p>
            </div>
          )}
        />
      )}
    </>
  );
}

AccessSimulator.propTypes = {
  userId: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([undefined, null])]),
  getConfig: PropTypes.func.isRequired,
  listPatients: PropTypes.func.isRequired,
  listOrgPatients: PropTypes.func.isRequired,
  getMyOrgs: PropTypes.func.isRequired,
  pointOrg: PropTypes.func.isRequired,
  unpointOrg: PropTypes.func.isRequired,
  createPatient: PropTypes.func.isRequired,
  updatePatient: PropTypes.func.isRequired,
  deletePatient: PropTypes.func.isRequired,
  setPatientScript: PropTypes.func.isRequired,
  clearPatientScript: PropTypes.func.isRequired,
  setOrgDefault: PropTypes.func.isRequired,
  reset: PropTypes.func.isRequired,
  sendUnalignmentEmail: PropTypes.func.isRequired,
  showNotification: PropTypes.func.isRequired,
  openConfirmModal: PropTypes.func.isRequired,
};

const mapDispatchToProps = dispatch => ({
  getConfig: orgUUID => dispatch(actions.getConfig(orgUUID)),
  listPatients: orgUUID => dispatch(actions.listPatients(orgUUID)),
  listOrgPatients: orgUUID => dispatch(actions.listOrgPatients(orgUUID)),
  getMyOrgs: () => dispatch(actions.getMyOrgs()),
  pointOrg: (body, orgUUID) => dispatch(actions.pointOrg(body, orgUUID)),
  unpointOrg: orgUUID => dispatch(actions.unpointOrg(orgUUID)),
  createPatient: (patient, orgUUID) => dispatch(actions.createPatient(patient, orgUUID)),
  updatePatient: (id, patient, orgUUID) => dispatch(actions.updatePatient(id, patient, orgUUID)),
  deletePatient: (id, orgUUID) => dispatch(actions.deletePatient(id, orgUUID)),
  setPatientScript: (id, operation, script, orgUUID) => dispatch(actions.setPatientScript(id, operation, script, orgUUID)),
  clearPatientScript: (id, operation, seq, orgUUID) => dispatch(actions.clearPatientScript(id, operation, seq, orgUUID)),
  setOrgDefault: (script, orgUUID) => dispatch(actions.setOrgDefault(script, orgUUID)),
  reset: orgUUID => dispatch(actions.reset(orgUUID)),
  sendUnalignmentEmail: (body, orgUUID) => dispatch(actions.sendUnalignmentEmail(body, orgUUID)),
  showNotification: (message, timeout, error) => dispatch(notificationActions.show(message, timeout, error)),
  openConfirmModal: data => dispatch(openModalAction(NewConfirmationModal.MODAL_NAME, data)),
});

const mapStateToProps = state => ({
  userId: state.auth?.profile?.id,
});

export default connect(mapStateToProps, mapDispatchToProps)(AccessSimulator);
